import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Calendar, CheckCircle, Clock, Phone, MapPin, ArrowRight, Plus, Car } from 'lucide-react';
import { motion } from 'framer-motion';

const MyPageComplete = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('oversikt');
  
  // Mock data - brukerens biler
  const myCars = [
    {
      id: 1,
      brand: 'Toyota',
      model: 'Corolla',
      year: 2018,
      regNumber: 'AB12345',
      kilometers: 150000,
      status: 'pending_test', // pending_test, test_booked, in_auction, sold
      statusText: 'Venter på test',
      addedDate: '2024-01-15'
    },
    {
      id: 2,
      brand: 'Volkswagen',
      model: 'Golf',
      year: 2020,
      regNumber: 'CD67890',
      kilometers: 85000,
      status: 'in_auction',
      statusText: 'Budrunde pågår',
      addedDate: '2024-01-10',
      highestBid: 285000,
      bidsCount: 12
    }
  ];

  const getStatusColor = (status) => {
    switch(status) {
      case 'pending_test': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'test_booked': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'in_auction': return 'bg-green-100 text-green-800 border-green-200';
      case 'sold': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusStep = (status) => {
    switch(status) {
      case 'pending_test': return 1;
      case 'test_booked': return 1;
      case 'in_auction': return 2;
      case 'sold': return 3;
      default: return 1;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Mine bilsalg</h1>
            <p className="text-gray-600">Oversikt over dine biler og hvor de er i prosessen</p>
          </div>
          <Button 
            onClick={() => navigate('/selg-bilen')}
            className="bg-gray-900 hover:bg-gray-800 gap-2"
          >
            <Plus className="w-4 h-4" />
            Legg til bil
          </Button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-8">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('oversikt')}
              className={`pb-3 px-1 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'oversikt' 
                  ? 'border-gray-900 text-gray-900' 
                  : 'border-transparent text-gray-500 hover:text-gray-900'
              }`}
            >
              Oversikt
            </button>
            <button
              onClick={() => setActiveTab('historikk')}
              className={`pb-3 px-1 font-medium text-sm transition-colors border-b-2 ${
                activeTab === 'historikk' 
                  ? 'border-gray-900 text-gray-900' 
                  : 'border-transparent text-gray-500 hover:text-gray-900'
              }`}
            >
              Historikk
            </button>
          </div>
        </div>

        {/* Cars List */}
        <div className="space-y-4">
          {myCars.map((car) => (
            <motion.div
              key={car.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-gray-300 transition-colors"
            >
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
                
                {/* Car Info */}
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 bg-gray-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Car className="w-8 h-8 text-gray-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-1">
                      {car.brand} {car.model} ({car.year})
                    </h3>
                    <p className="text-sm text-gray-600 mb-2">
                      {car.regNumber} • {car.kilometers.toLocaleString('nb-NO')} km
                    </p>
                    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(car.status)}`}>
                      {car.statusText}
                    </div>
                  </div>
                </div>

                {/* Status/Action */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  
                  {/* If in auction - show bid info */}
                  {car.status === 'in_auction' && (
                    <div className="text-right">
                      <p className="text-2xl font-bold text-gray-900">
                        {car.highestBid.toLocaleString('nb-NO')} kr
                      </p>
                      <p className="text-sm text-gray-600">{car.bidsCount} bud mottatt</p>
                    </div>
                  )}

                  {/* Action button */}
                  <div>
                    {car.status === 'pending_test' && (
                      <Button className="bg-gray-900 hover:bg-gray-800 gap-2">
                        <Calendar className="w-4 h-4" />
                        Book test
                      </Button>
                    )}
                    {car.status === 'in_auction' && (
                      <Button variant="outline" className="gap-2">
                        Se bud
                        <ArrowRight className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              {/* Progress Steps */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <div className="flex items-center">
                  {[1, 2, 3].map((step, index) => {
                    const currentStep = getStatusStep(car.status);
                    const isCompleted = step < currentStep;
                    const isCurrent = step === currentStep;
                    
                    return (
                      <React.Fragment key={step}>
                        <div className="flex flex-col items-center">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                            isCompleted || isCurrent 
                              ? 'bg-gray-900 text-white' 
                              : 'bg-gray-200 text-gray-500'
                          }`}>
                            {isCompleted ? <CheckCircle className="w-4 h-4" /> : step}
                          </div>
                          <span className={`text-xs mt-2 text-center ${
                            isCurrent ? 'text-gray-900 font-medium' : 'text-gray-500'
                          }`}>
                            {step === 1 ? 'Test' : step === 2 ? 'Budrunde' : 'Solgt'}
                          </span>
                        </div>
                        {index < 2 && (
                          <div className={`flex-1 h-1 mx-2 ${
                            isCompleted ? 'bg-gray-900' : 'bg-gray-200'
                          }`} />
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Empty state if no cars */}
        {myCars.length === 0 && (
          <div className="bg-white border border-gray-200 rounded-2xl p-12 text-center">
            <Car className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-gray-900 mb-2">Ingen biler ennå</h3>
            <p className="text-gray-600 mb-6">
              Legg til din første bil for å komme i gang
            </p>
            <Button 
              onClick={() => navigate('/selg-bilen')}
              className="bg-gray-900 hover:bg-gray-800 gap-2"
            >
              <Plus className="w-4 h-4" />
              Legg til bil
            </Button>
          </div>
        )}

        {/* Help Section */}
        <div className="mt-8 bg-gray-50 border border-gray-200 rounded-2xl p-6">
          <h3 className="font-bold text-gray-900 mb-4">Hva skjer i hver fase?</h3>
          
          <div className="space-y-4">
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-sm font-bold">1</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-1">Test av bilen</h4>
                <p className="text-sm text-gray-600">
                  Book gratis test hos Viking Kontroll eller NAF. Vi sjekker bilen og tar bilder.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-gray-600 text-sm font-bold">2</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-1">Budrunde</h4>
                <p className="text-sm text-gray-600">
                  Bilen legges ut til over 2 000 forhandlere som konkurrerer om å gi beste pris.
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-gray-600 text-sm font-bold">3</span>
              </div>
              <div>
                <h4 className="font-semibold text-gray-900 text-sm mb-1">Bilen solgt</h4>
                <p className="text-sm text-gray-600">
                  Du velger beste bud. Pengene kommer på konto i løpet av få dager.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Contact */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600 mb-2">Trenger du hjelp?</p>
          <div className="flex items-center justify-center gap-4">
            <a href="tel:47494900" className="text-sm font-medium text-gray-900 hover:underline flex items-center gap-2">
              <Phone className="w-4 h-4" />
              47 49 49 00
            </a>
            <span className="text-gray-300">•</span>
            <a href="mailto:hei@digicar.no" className="text-sm font-medium text-gray-900 hover:underline">
              hei@digicar.no
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyPageComplete;
